/* Copyright (c) Trimble Inc. All rights reserved. */

import { create } from 'zustand';
import { TokenTable } from '../tables/Token.table';
import { type TokenDetails } from '../models/Token.model';
import { Logger } from '@tcp-base/toolkit';

interface TokenStoreState {
  tokenDetails: TokenDetails;
  saveTokenDetails: (details: TokenDetails) => Promise<void>;
  getTokenDetails: () => Promise<TokenDetails>;
  removeTokenDetails: () => Promise<void>;
}

export const useTokenStore = create<TokenStoreState>((set, get) => ({
  tokenDetails: {},

  saveTokenDetails: async (details: TokenDetails): Promise<void> => {
    try {
      // Update zustand state
      set({ tokenDetails: details });

      // Save to IndexedDB using TokenTable singleton
      await TokenTable.saveTokenDetails(details);
    } catch (error) {
      Logger.error('Failed to save token details:', error);
      throw error;
    }
  },

  getTokenDetails: async (): Promise<TokenDetails> => {
    const { tokenDetails } = get();

    // Check if zustand has non-empty token details (at least access_token)
    if (tokenDetails.access_token) {
      return tokenDetails;
    }

    try {
      // If zustand is empty, check IndexedDB via TokenTable
      const dbTokenDetails = await TokenTable.getTokenDetails();

      // If IndexedDB has token data, store it in zustand state
      if (dbTokenDetails.access_token) {
        set({ tokenDetails: dbTokenDetails });
        return dbTokenDetails;
      }

      // Return empty if no data found anywhere
      return tokenDetails;
    } catch (error) {
      Logger.error('Failed to get token details from IndexedDB Store:', error);
      return tokenDetails;
    }
  },

  removeTokenDetails: async (): Promise<void> => {
    try {
      // Clear zustand state immediately
      set({ tokenDetails: {} });

      // Remove from IndexedDB using TokenTable singleton
      await TokenTable.removeTokenDetails();
    } catch (error) {
      Logger.error('Failed to remove token details:', error);
      throw error;
    }
  },
}));

/**
 * Helper function to get access token from TokenStore (synchronous)
 * Note: This only returns from zustand state. Use getTokenDetails() for IndexedDB fallback.
 */
export const getAccessToken = (): string | undefined => {
  return useTokenStore.getState()?.tokenDetails?.access_token;
};

/**
 * Helper function to remove all token details from both zustand state and IndexedDB
 */
export const removeTokenDetails = (): Promise<void> => {
  return useTokenStore.getState()?.removeTokenDetails();
};
